// Monotonic counter used to give each recommendations wrapper a unique id when
// it lacks one, so its slider nav can be scoped to just that wrapper.
let navScopeSeq = 0;

export class Recommendations {
  // Accepts either a CSS selector (legacy) or a wrapper element directly, so a
  // page can host multiple recommendation sliders (e.g. the Recommendations
  // section plus a Products Slider block set to "Recommended").
  constructor(target = ".recommended-products-wrapper") {
    this.section = typeof target === "string" ? document.querySelector(target) : target;
    this.observer = null;
    this.observerTarget = null;
    this.hasLoaded = false;
    this.isLoading = false;
    this.fallbackTimer = null;

    // Guard against double-initialising the same wrapper.
    if (this.section && this.section.dataset.recommendationsInitialized !== "true") {
      this.section.dataset.recommendationsInitialized = "true";
      this.observerTarget = this.resolveObserverTarget();
      this.init();
    }
  }

  resolveObserverTarget() {
    if (!this.section) return null;
    return (
      this.section.closest("[data-recommendations-observe]") ||
      this.section.closest(".product-recommendations") ||
      this.section.parentElement ||
      this.section
    );
  }

  init() {
    const target = this.observerTarget || this.section;
    if (!target) return;

    if (typeof window.IntersectionObserver !== "function") {
      this.loadRecommendations();
      return;
    }

    const options = {
      rootMargin: "400px",
    };

    this.observer = new IntersectionObserver((entries) => {
      this.handleIntersection(entries);
    }, options);

    this.observer.observe(target);

    // Fallback: if the element has no dimensions, IntersectionObserver will never fire.
    if (target.getBoundingClientRect().height === 0) {
      requestAnimationFrame(() => {
        if (!this.hasLoaded) {
          this.loadRecommendations();
        }
      });
    }

    // Absolute fallback to guard against observers not firing in edge cases.
    this.fallbackTimer = window.setTimeout(() => {
      if (!this.hasLoaded) {
        this.loadRecommendations();
      }
    }, 4000);
  }

  handleIntersection(entries) {
    if (!Array.isArray(entries)) return;

    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      if (this.observer) {
        this.observer.unobserve(entry.target);
      }
      this.loadRecommendations();
    });
  }

  async loadRecommendations() {
    if (this.isLoading || this.hasLoaded) return;

    const url = this.section.dataset.url;

    if (!url) {
      console.error("Recommendations: No URL found in dataset");
      this.hasLoaded = true;
      return;
    }

    this.isLoading = true;

    try {
      const response = await fetch(url, { credentials: "same-origin" });

      if (response.status === 401) {
        console.warn(
          "Recommendations: Request was unauthorized. This often happens when viewing a password-protected preview.",
        );
        this.onNoRecommendations();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      const html = document.createElement("div");
      html.innerHTML = text;

      // The fetched section HTML can contain more than one recommendations
      // wrapper (e.g. the PDP's sold-out panel slider alongside the "Goes
      // Well With" block, both rendered inside the same product section) —
      // match this instance's own wrapper by id so each slider gets its own
      // content/config instead of every instance grabbing the first one.
      const instanceId = this.section.dataset.recommendationsId;
      let recommendations = instanceId
        ? html.querySelector(
            `.recommended-products-wrapper[data-recommendations-id="${CSS.escape(instanceId)}"]`,
          )
        : null;

      // Fall back to the first wrapper in the response (single-wrapper pages).
      if (!recommendations) {
        recommendations = html.querySelector(".recommended-products-wrapper");
      }

      // If no wrapper found, fall back to .product-recommendations
      if (!recommendations) {
        recommendations = html.querySelector(".product-recommendations");
      }

      if (recommendations && recommendations.innerHTML.trim().length) {
        // Insert only the contents, not the wrapper itself
        this.section.innerHTML = recommendations.innerHTML;
        this.onRecommendationsLoaded();
      } else {
        this.onNoRecommendations();
      }
    } catch (error) {
      console.error("Recommendations: Failed to load recommendations", error);
      this.onLoadError(error);
    } finally {
      if (this.fallbackTimer) {
        clearTimeout(this.fallbackTimer);
        this.fallbackTimer = null;
      }
      this.isLoading = false;
      this.hasLoaded = true;
    }
  }

  onRecommendationsLoaded() {
    this.initLoadedSlider();
  }

  resolveLiveNavScope(root, slider) {
    if (!slider) return "";

    // Scope the nav to this slider's OWN recommendations container rather than the
    // whole Shopify section. Multiple recommendation sliders can live in one
    // section (e.g. the sold-out panel's slider + the PDP recommendations block),
    // and a section-wide scope makes them cross-bind each other's arrows — the
    // last one initialised steals the buttons and the others appear dead. The nav
    // buttons always live inside .recommended-products-wrapper, so scoping there
    // isolates each slider.
    const localScope =
      slider.closest(".recommended-products-wrapper") ||
      root ||
      slider.closest(".product-recommendations") ||
      null;

    // Only use the local scope if it actually contains nav buttons of its own.
    // Some callers (e.g. a Products Slider block set to "Recommended", which
    // supplies its own heading + nav-arrows block outside the wrapper) render
    // with no heading, so recommendations.liquid skips its internal nav
    // entirely — scoping there would bind nothing and leave the real arrows dead.
    if (localScope?.querySelector(".blaze-button-next, .blaze-button-prev")) {
      if (!localScope.id) {
        navScopeSeq += 1;
        localScope.id = `rec-nav-scope-${navScopeSeq}`;
      }
      return `#${localScope.id}`;
    }

    // Fallback: no local container found, or it has no nav buttons of its own
    // — scope to the section instead (legacy behaviour) so an external
    // nav-arrows block can still bind.
    const sectionScope =
      slider.closest('[id^="shopify-section-"]') ||
      root?.closest?.('[id^="shopify-section-"]') ||
      null;

    return sectionScope?.id ? `#${sectionScope.id}` : "";
  }

  ensureNavigationDataset(root, slider) {
    if (!slider) return;

    const liveScope = this.resolveLiveNavScope(root, slider);
    if (liveScope) {
      slider.dataset.sliderNavScope = liveScope;
    }

    if (!slider.dataset.sliderNavNext) {
      slider.dataset.sliderNavNext = ".blaze-button-next";
    }

    if (!slider.dataset.sliderNavPrev) {
      slider.dataset.sliderNavPrev = ".blaze-button-prev";
    }
  }

  initLoadedSlider() {
    const root = this.section;
    if (!root || typeof root.querySelector !== "function") return;

    const slider = root.querySelector("[data-blaze-slider]");
    if (!slider) return;

    this.ensureNavigationDataset(root, slider);

    const controller = window.themeBlazeSliderController;
    if (controller && typeof controller.init === "function") {
      controller.init(root);
      return;
    }

    import("@theme/blaze-slider-controller")
      .then(({ blazeSliderController }) => {
        blazeSliderController.init(root);
      })
      .catch((error) => {
        console.warn("Recommendations: failed to initialize slider", error);
      });
  }

  onNoRecommendations() {
    // Hook for when no recommendations are found
    // Can be overridden or extended
  }

  onLoadError(error) {
    // Hook for when loading fails
    // Can be overridden or extended
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }
  }
}

// Cart-driven recommendation sliders (e.g. the cart page's "You may also like")
// base their recommendations on the first cart item. When the cart empties there
// is nothing left to recommend against, so hide the whole slider section. Only
// sliders marked data-recommended-products="cart" are touched, so PDP / product-
// driven sliders are never affected. The generic Slider section isn't among the
// sections the cart re-renders, hence this client-side sync.
let cartVisibilityBound = false;

function resolveCartItemCount(detail) {
  if (detail && typeof detail.itemCount === "number") return detail.itemCount;
  const stateCount = window.cart?.cart?.item_count;
  if (typeof stateCount === "number") return stateCount;
  return null;
}

function syncCartDrivenRecommendations(detail) {
  const sliders = document.querySelectorAll('[data-recommended-products="cart"]');
  if (!sliders.length) return;

  const count = resolveCartItemCount(detail);
  if (count === null) return;

  sliders.forEach((wrapper) => {
    const section = wrapper.closest(".slider-section") || wrapper;
    section.classList.toggle("hidden", count === 0);
  });
}

function initCartDrivenRecommendationsVisibility() {
  if (cartVisibilityBound) return;
  cartVisibilityBound = true;
  ["cart:count", "cart:updated", "cart:sections-rendered"].forEach((name) => {
    window.addEventListener(name, (event) => syncCartDrivenRecommendations(event?.detail));
  });
}

// Initialise every recommendations wrapper that needs an async fetch. Wrappers
// rendered server-side (no data-url) are skipped; the constructor guard makes
// repeat calls harmless.
export function initRecommendations(root = document) {
  const scope = root && typeof root.querySelectorAll === "function" ? root : document;
  scope.querySelectorAll(".recommended-products-wrapper[data-url]").forEach((wrapper) => {
    new Recommendations(wrapper);
  });
  initCartDrivenRecommendationsVisibility();
}

// Auto-initialize if script is loaded directly (backward compatibility)
if (typeof module === "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initRecommendations());
  } else {
    initRecommendations();
  }
}
